import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import React, { useImperativeHandle } from 'react';

import { useEventCallback } from '@gitbook/util-react-hooks';

import type { Scroller } from './types';

/**
 * Scollable list of OpenAPI selectable items.
 */
export function OpenAPISelectionList<T>(props: {
    items: T[];
    children: (props: {
        item: T;
        index: number;
        ref: (element: Element | undefined | null) => void;
    }) => React.ReactElement;
    scrollerRef?: React.RefObject<Scroller>;
    className?: string;
}) {
    const { items, className } = props;
    const containerRef = React.useRef<HTMLDivElement | null>(null);

    const options = getVirtualizerOptions(items[0]);

    const virtualizer = useVirtualizer({
        count: items.length,
        overscan: options.overscan,
        getScrollElement: () => containerRef.current,
        estimateSize: () => options.size,
        gap: 8,
    });

    const scrollToIndex = useEventCallback(virtualizer.scrollToIndex);

    useImperativeHandle(props.scrollerRef, () => ({ scrollToIndex }), [scrollToIndex]);

    const virtualItems = virtualizer.getVirtualItems();

    return (
        <div
            ref={containerRef}
            className={clsx('h-full flex-1 overflow-y-auto contain-strict', className)}
        >
            <div
                style={{
                    height: virtualizer.getTotalSize(),
                    width: '100%',
                    position: 'relative',
                }}
            >
                <div
                    className="flex flex-col gap-2"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
                    }}
                >
                    {virtualItems.map((virtualRow) =>
                        props.children({
                            item: items[virtualRow.index],
                            index: virtualRow.index,
                            ref: virtualizer.measureElement,
                        })
                    )}
                </div>
            </div>
        </div>
    );
}

function isOperation(item: unknown): item is { method: string; path: string } {
    return (
        !!item &&
        typeof item === 'object' &&
        'method' in item &&
        typeof item.method === 'string' &&
        'path' in item &&
        typeof item.path === 'string'
    );
}

function getVirtualizerOptions(item: unknown) {
    if (!item) {
        return { overscan: 0, size: 0 };
    }

    if (isOperation(item)) {
        return { overscan: 5, size: 80 };
    }

    return { overscan: 10, size: 40 };
}
